'use client'

import type { ReactNode } from 'react'

import useStore from '@/stores/Store'

import '../sections.css'

import parse from 'html-react-parser'

import { useIsMobile } from '@/hooks/useIsMobile'

interface JumbotronProps {
  badgeText?: string
  title: string
  buttonText?: ReactNode
  footNotes?: ReactNode
  /** Primary (desktop) background image URL. */
  backgroundImage?: string
  /**
   * Optional mobile background image. When provided it replaces
   * `backgroundImage` at ≤767px. Selecting it here (inside this already-client
   * component) keeps callers as Server Components — they must not pick the
   * image in JS themselves.
   */
  mobileBackgroundImage?: string
  /** Optional secondary background layered over `backgroundImage`. */
  uniqueBg?: string
  isFullWidth?: boolean
}

function Jumbotron({
  badgeText,
  title,
  buttonText,
  footNotes,
  backgroundImage,
  mobileBackgroundImage,
  uniqueBg,
  isFullWidth
}: JumbotronProps) {
  const { formRef } = useStore()

  // useIsMobile (stateful, mounted-gated): a bare `window.innerWidth` check
  // evaluates once per render with no state, so after hydration it stays on
  // the SSR (desktop) value and ships the desktop background to phones.
  const isMobile = useIsMobile({ maxWidth: 767 })
  const resolvedBackgroundImage =
    isMobile && mobileBackgroundImage ? mobileBackgroundImage : backgroundImage
  const backgroundStyle = resolvedBackgroundImage
    ? {
        // Only layer in the secondary background when uniqueBg is provided —
        // otherwise the template literal stringifies `undefined`, emitting
        // `url(undefined)`, which the browser fetches and 404s on.
        backgroundImage: uniqueBg
          ? `url(${resolvedBackgroundImage}), url(${uniqueBg})`
          : `url(${resolvedBackgroundImage})`,
        backgroundPosition: uniqueBg
          ? isFullWidth && isMobile
            ? 'center -105px, center center'
            : 'bottom right, bottom center'
          : isFullWidth && isMobile
            ? 'center -105px'
            : 'bottom right',
        backgroundSize: uniqueBg
          ? isFullWidth && isMobile
            ? 'cover, cover'
            : 'contain, cover'
          : isFullWidth && isMobile
            ? 'cover'
            : 'contain',
        backgroundRepeat: uniqueBg ? 'no-repeat, no-repeat' : 'no-repeat',
        width: isFullWidth && isMobile ? '100%' : 'auto',
        minHeight: isFullWidth && isMobile ? '60vh' : '40vh'
      }
    : {}

  return (
    // <div className="jumbotron">
    <div className="background-color">
      <div className="background-img" style={backgroundStyle}>
        <div className="container">
          <div className={`jumbotron-container ${!backgroundImage && 'row-height-unsetter'}`}>
            {badgeText && <div className="jumbotron-title col-xl-2">{parse(badgeText)}</div>}
            <h1 className="jumbotron-subtitle col-xl-6 text-center text-xl-start">
              {parse(title)}
            </h1>

            {buttonText && (
              <button
                className="btn btn-primary btn-lg applybtn"
                type="button"
                onClick={() => {
                  if (formRef.current) {
                    const offset = 410 // Adjust this value as needed
                    const topPosition =
                      formRef.current.getBoundingClientRect().top + window.pageYOffset - offset
                    window.scrollTo({
                      top: topPosition,
                      behavior: 'smooth'
                    })
                  }

                  // formRef?.current?.scrollIntoView({
                  //   block: "start",
                  //   offsetTop: 20,
                  // })
                }}
              >
                {buttonText}
              </button>
            )}
            {footNotes && (
              <div className="jumbotron-note">
                <i className="bi bi-shield" style={{ margin: '5px' }}></i>
                {footNotes}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    // </div>
  )
}

export default Jumbotron
